<template>
  <div ref="loader">
    <div class="w-100" v-bind:class="mainHeaderBgClasses">
      <b-container class="container-lg">

        <!-- Hostname and refresh button -->
        <b-row class="p-0 pt-2">
          <b-col class="col-9 text-start min-width-0">
            <h5 class="text-truncate">
              <i class="fa-solid fa-chevron-left" @click="goBack"></i>
              {{ mergedHost.name }}
            </h5>
          </b-col>
          <b-col class="col-3 text-end">
            <button type="button" class="btn btn-sm" @click="refreshData"
                    v-bind:class="mainHeaderRefreshBtnClasses">
              <i class="fa-solid fa-arrows-rotate"
                 :class="{'fa-spin': loading}"></i>
            </button>
          </b-col>
        </b-row>

        <!-- Not in monitoring text -->
        <b-row class="p-0 pt-2 pb-2" v-if="hoststatus.isInMonitoring === false">
          <b-col class="col-12">
            <i class="fa-solid fa-triangle-exclamation me-1"></i>
            {{ $t('Not-in-monitoring') }}
          </b-col>
        </b-row>

        <!-- time information -->
        <b-row class="p-0 pt-2 pb-2" v-if="hoststatus.isInMonitoring">
          <b-col class="col-12">
            <i class="fa-solid fa-clock-rotate-left me-1"></i>
            {{ $t('state-since') }}:
            <span>
              {{ hoststatus.last_state_change }}
            </span>
          </b-col>
          <b-col class="col-12">
            <!-- width: 16 to make all icons the same size -->
            <i class="fa-solid fa-arrow-turn-up fa-rotate-270 me-1" style="width:16px;"></i>
            {{ $t('last-check') }}:
            <span>
              {{ hoststatus.lastCheck }}
            </span>
          </b-col>
          <b-col class="col-12">
            <i class="fa-solid fa-arrow-turn-down fa-rotate-270 me-1" style="width:16px;"></i>
            {{ $t('next-check') }}:
            <span
                v-show="mergedHost.active_checks_enabled && mergedHost.is_satellite_host === false">
              <span>
                {{ hoststatus.nextCheck }}
              </span>
            </span>
            <span
                v-show="!mergedHost.active_checks_enabled || mergedHost.is_satellite_host === true">
              {{ $t('n/a') }}
            </span>
          </b-col>
        </b-row>

        <!-- icons -->
        <b-row class="p-0 pt-2 pb-2">
          <b-col class="text-start">
            <div class="icon-group" role="group">
              <div class="icon">
                <priority :priority="mergedHost.priority" :as-bar="true"></priority>
              </div>
            </div>
          </b-col>
          <b-col class="text-end" v-if="hoststatus.isInMonitoring">
            <div class="icon-group" role="group">
              <div class="icon">
                <i v-show="acknowledgement.is_sticky === false && hoststatus.problemHasBeenAcknowledged === true"
                   class="icon fa-regular fa-user"></i>

                <i v-show="acknowledgement.is_sticky === true && hoststatus.problemHasBeenAcknowledged === true"
                   class="icon fa fa-user"></i>

                <i class="icon fa-solid fa-power-off"
                   v-show="hoststatus.scheduledDowntimeDepth > 0"></i>

                <div class="icon icon-stack"
                     v-show="hoststatus.notifications_enabled">
                  <i class="fas fa-envelope"></i>
                  <i class="fas fa-check opacity-100 fa-xs text-success cornered cornered-lr"></i>
                </div>

                <div class="icon icon-stack"
                     v-show="!hoststatus.notifications_enabled">
                  <i class="fas fa-envelope"></i>
                  <i class="fas fa-times opacity-100 fa-xs text-danger cornered cornered-lr"></i>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>

      </b-container>
    </div>

    <b-container class="container-lg mt-0 p-0 pt-4">
      <b-card class="border-bottom-0 rounded-bottom-0">
        <b-button-group class="w-100">
          <button type="button"
                  :class="{ 'btn btn-primary': selectedTab === 'tab1', 'btn btn-light': selectedTab !== 'tab1' }"
                  @click="setTab('tab1')">
            <i class="fa-solid fa-info"></i>
            {{ $t('Details') }}
          </button>
          <button type="button"
                  :class="{ 'btn btn-primary': selectedTab === 'tab2', 'btn btn-light': selectedTab !== 'tab2' }"
                  @click="setTab('tab2')">
            <i class="fa-solid fa-cogs"></i>
            {{ $t('Services') }}
          </button>
          <button type="button" v-show="actionPermissions"
                  :class="{ 'btn btn-primary': selectedTab === 'tab3', 'btn btn-light': selectedTab !== 'tab3' }"
                  @click="setTab('tab3')">
            <i class="fa-solid fa-terminal"></i>
            {{ $t('actions') }}
          </button>

        </b-button-group>
      </b-card>
      <b-card class="rounded-top-0">
        <div v-show="selectedTab === 'tab1'">
          <b-card no-body v-show="mergedHost.disabled">
            <div class="alert-warning alert-warning-shadow ps-1 pt-2 pb-2" role="alert">
              {{ $t('attention') }} <br>
              {{ $t('currently-disabled', {obj: 'host'}) }}
            </div>
          </b-card>
          <b-card no-body v-show="hasParentHostProblems" class="pt-2">
            <div class="alert-warning alert-warning-shadow ps-1 pt-2 pb-2" role="alert">
              <i class="fa fa-exclamation-triangle"></i>
              {{ $t('Problem with parent host detected!') }}
              <ul>
                <li v-for="parentHostProblem in parentHostProblems"
                    v-bind:key="parentHostProblem.id">
                  <router-link :to="{name: 'hostsDetails', params: {id: parentHostProblem.id}}">
                    {{ parentHostProblem.name }}
                  </router-link>
                </li>
              </ul>
            </div>
          </b-card>

          <div v-if="!hoststatus.isInMonitoring"
               class="w-100 border-primary text-primary shadow-primary p-2 rounded text-center">
            {{ $t('This host is currently not in monitoring.') }}
          </div>

          <div v-if="hoststatus.isInMonitoring">

            <b-list-group-item
                class="list-group-item-details p-0 pt-1 pb-1 border-none border-bottom"
                v-if="hoststatus.scheduledDowntimeDepth > 0">
              <div class="mb-3">
                <p class="mb-1 overflow-auto">
                  <i class="fa-solid fa-power-off"></i>
                  {{ $t('text.maintenance-period', {object: 'Host'}) }}
                </p>
                <small class="mb-2 overflow-auto"
                       v-html="$t('text.downtime-set',{owner: downtime.authorName, duration: downtime.durationHuman, start: downtime.scheduledStartTime, end: downtime.scheduledEndTime,  comment: downtime.commentData})">
                </small>
              </div>
            </b-list-group-item>

            <b-list-group-item
                class="list-group-item-details p-0 pt-1 pb-1 border-none border-bottom"
                v-if="hoststatus.problemHasBeenAcknowledged">
              <div>
                <p class="mb-1 overflow-auto">
                  <i v-show="!acknowledgement.is_sticky"
                     class="fa-regular fa-user"></i>
                  <i v-show="acknowledgement.is_sticky"
                     class="fa-solid fa-user"></i>

                  {{ $t('text.state-is-acknowledged', {object: 'Host'}) }}
                  <span v-if="acknowledgement.is_sticky">
                    ({{ $t('forms.sticky') }})
                  </span>
                </p>
                <small class="mb-1 overflow-auto"
                       v-html="$t('text.acknowledgement-set',{owner: acknowledgement.author_name, time: acknowledgement.entry_time, comment: acknowledgement.commentDataHtml})">
                </small>
              </div>
            </b-list-group-item>


            <b-list-group-item
                class="list-group-item-details p-0 pt-1 pb-1 border-none border-bottom">
              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('state') }}
                </small>
                <h5 class="mb-1" v-bind:class="hoststatus.humanState">
                  {{ $t(hoststatus.humanState) }}
                </h5>
              </div>

              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('state-type') }}
                </small>
                <small class="mb-1" v-if="hoststatus.isHardstate">{{ $t('hard-state') }}
                  ({{ hoststatus.current_check_attempt }}/{{
                    hoststatus.max_check_attempts
                  }})
                </small>
                <small class="mb-1" v-if="!hoststatus.isHardstate">{{ $t('soft-state') }}
                  ({{ hoststatus.current_check_attempt }}/{{
                    hoststatus.max_check_attempts
                  }})
                </small>
              </div>

              <div class="d-flex w-100 justify-content-between"
                   v-show="hoststatus.currentState > 0">
                <small>
                  {{ $t('last-time') }} <span class="badge badge-success bg-up">
                  {{ $t('up') }}
                </span>
                </small>
                <small>
                  {{ hoststatus.last_time_up }}
                </small>
              </div>
              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('flap-detection-enabled') }}
                </small>
                <small>
                  <span class="badge bg-up"
                        v-if="hoststatus.flap_detection_enabled">
                    {{ $t('yes') }}
                  </span>
                  <span class="badge bg-down"
                        v-if="!hoststatus.flap_detection_enabled">
                    {{ $t('no') }}
                  </span>
                </small>
              </div>

              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('check-period') }}
                </small>
                <small>
                  {{ checkPeriod.name }}
                </small>
              </div>
              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('check-interval') }}
                </small>
                <small>
                  {{ mergedHost.checkIntervalHuman }}
                </small>
              </div>
              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('retry-interval') }}
                </small>
                <small>
                  {{ mergedHost.retryIntervalHuman }}
                </small>
              </div>
            </b-list-group-item>


            <b-list-group-item
                class="list-group-item-details p-0 pt-1 pb-1 border-none border-bottom">
              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('Address') }}
                </small>
                <small>
                  {{ mergedHost.address }}
                </small>
              </div>

              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('commandline') }}
                </small>
              </div>
              <div class="d-flex w-100 justify-content-between code-block output-style">
                {{ mergedHost.hostCommandLine }}
              </div>

              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('output') }}
                </small>
              </div>
              <div class="d-flex w-100 justify-content-between code-block output-style">
                {{ hoststatus.output }}
              </div>
            </b-list-group-item>


            <b-list-group-item
                class="p-0 pt-1 pb-1 border-none">
              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('notification-period') }}
                </small>
                <small>
                  {{ notifyPeriod.name }}
                </small>
              </div>
              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('notification-interval') }}
                </small>
                <small>
                  {{ mergedHost.notificationIntervalHuman }}
                </small>
              </div>
              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('notifications-enabled') }}
                </small>
                <small>
                  <span class="badge bg-up"
                        v-if="hoststatus.notifications_enabled">
                    {{ $t('yes') }}
                  </span>
                  <span class="badge bg-down"
                        v-if="!hoststatus.notifications_enabled">
                    {{ $t('no') }}
                  </span>
                </small>
              </div>
              <div class="d-flex w-100 justify-content-between">
                <small>
                  {{ $t('notify-on') }}
                </small>
              </div>
              <div class="d-flex w-100 justify-content-end">
                <small class="ms-auto">
                  <span class="badge bg-up me-1"
                        v-if="mergedHost.notify_on_recovery">
                    {{ $t('recover') }}
                  </span>
                  <span class="badge bg-down me-1"
                        v-if="mergedHost.notify_on_down">
                    {{ $t('down') }}
                  </span>
                  <span class="badge bg-unknown me-1"
                        v-if="mergedHost.notify_on_unreachable">
                    {{ $t('unreachable') }}
                  </span>
                  <span class="badge me-1"
                        v-if="mergedHost.notify_on_downtime">
                    {{ $t('downtime') }}
                  </span>
                  <span class="badge bg-primary"
                        v-if="mergedHost.notify_on_flapping">
                    {{ $t('flapping') }}
                  </span>
                </small>
              </div>
            </b-list-group-item>
            <b-list-group-item>
              <div>
                <small>{{ $t('Contacts') }}</small>
              </div>
              <div class="d-flex w-100" v-for="contact in mergedHost.contacts">
                <span class="badge bg-primary-custom text-white mb-2" @click="contactInfo(contact)">
                  {{ contact.name }} <i class="fa-solid fa-eye"></i>
                </span>
              </div>
            </b-list-group-item>
          </div>
        </div>
        <div v-show="selectedTab === 'tab2'">
          <b-list-group>
            <b-list-group-item>
              <div class="row">
                <div class="col">
                  <b-button v-b-toggle.filter class="m-0 w-100" variant="primary">
                    <i class="fa-solid fa-filter"></i>
                    {{ $t('Filter') }}
                  </b-button>
                </div>
              </div>
              <b-collapse id="filter">
                <div class="d-flex w-100 mt-2">
                  <b-form-input v-model="servicenameFilter" :placeholder="$t('servicename')"></b-form-input>
                </div>
                <div class="d-flex w-100 mt-2">
                  <multiselect v-model="servicestates"
                               track-by="value"
                               :multiple="true"
                               :close-on-select="true"
                               :show-labels="false"
                               label="label"
                               placeholder="Service status"
                               :options="servicestateFilterOptions"
                               :searchable="false"
                               :allow-empty="true">
                  </multiselect>
                </div>
                <div class="mt-2">
                  <b-button
                      class="w-100"
                      variant="outline-danger"
                      v-on:click="allServicesFilter">{{ $t('filter.reset-filter') }}
                  </b-button>
                </div>
              </b-collapse>

            </b-list-group-item>
          </b-list-group>
          <b-list-group
              v-for="service in services"
              v-bind:key="service.Service.id"
              class="mt-2">

            <div class="flex-column align-items-start">
              <div class="list-group-item flex-column align-items-start list-group-item-action ps-0">

                <div class="d-flex flex-row min-h-110">
                  <div class="p-2">
                    <Servicestatus v-bind:current_state="service.Servicestatus.currentState"
                                   v-bind:human_state="service.Servicestatus.humanState"></Servicestatus>
                  </div>
                  <div class="min-width-0">
                    <div class="row p-2">
                      <div class="col-12 text-truncate h5">
                        <router-link
                            :to="{name: 'servicesDetails', params: {id: service.Service.id}}">
                          {{ service.Service.servicename }}
                        </router-link>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 text-end">
                        <div class="icon-group" role="group">
                          <div class="icon">
                            <priority
                                :priority="service.Service.priority"></priority>

                            <i v-show="service.Servicestatus.acknowledgement_type === 1 && service.Servicestatus.problemHasBeenAcknowledged === true"
                               class="icon fa-regular fa-user"></i>

                            <i v-show="service.Servicestatus.acknowledgement_type === 2 && service.Servicestatus.problemHasBeenAcknowledged === true"
                               class="icon fa fa-user"></i>

                            <i class="icon fa-solid fa-power-off"
                               v-show="service.Servicestatus.scheduledDowntimeDepth > 0"></i>

                            <div class="icon icon-stack"
                                 v-show="service.Servicestatus.notifications_enabled">
                              <i class="fas fa-envelope"></i>
                              <i class="fas fa-check opacity-100 fa-xs text-success cornered cornered-lr"></i>
                            </div>

                            <div class="icon icon-stack"
                                 v-show="!service.Servicestatus.notifications_enabled">
                              <i class="fas fa-envelope"></i>
                              <i class="fas fa-times opacity-100 fa-xs text-danger cornered cornered-lr"></i>
                            </div>
                          </div>
                        </div>

                        <date-switch v-bind:css_class="service.Servicestatus.humanState"
                                     v-bind:status="service.Servicestatus"></date-switch>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </b-list-group>

          <b-list-group class="mt-3" ref="noMass">
            <div v-if="services.length > 0">
              <div class="d-flex w-100 justify-content-between">
                <small>{{ $t('page') }} {{ services_paging.page }}</small>
                <small>{{ $t('pagesize') }} {{ services_paging.limit }}</small>
              </div>
              <div class="d-flex w-100 justify-content-center mt-4">
                <vue-awesome-paginate
                    :total-items="services_paging.count"
                    :items-per-page="25"
                    :max-pages-shown="5"
                    v-model="services_paging.page"
                    @click="getServiceData"
                />
              </div>
            </div>
            <b-list-group-item v-if="services.length === 0">
              <h5 class="mb-1">{{ $t('noitems') }}</h5>
            </b-list-group-item>
          </b-list-group>
        </div>

        <actions v-show="selectedTab === 'tab3'" :hostuuid="mergedHost.uuid" :hostid="mergedHost.id"
                 :hoststatus="hoststatus.current_state" :satelliteId="mergedHost.satellite_id"
                 :status="hoststatus" :services="services" :downtime="downtime"
                 @refresh-event="refreshData()">

        </actions>
      </b-card>
    </b-container>
  </div>
</template>

<script>

import Actions from "./externalcommands/HostExternalCommands";
import Priority from "../utils/Priority";
import Servicestatus from "../utils/Servicestatus";
import DateSwitch from "../utils/DateSwitch";
import { useToast } from "vue-toastification";
import { VueAwesomePaginate } from "vue-awesome-paginate";
import Multiselect from 'vue-multiselect';
import { debounce } from '../../helpers';

export default {
  name: "HostsDetails",
  components: {Multiselect, VueAwesomePaginate, DateSwitch, Servicestatus, Priority, Actions},
  data() {
    return {
      selectedTab: 'tab1',
      rendertabs: true,
      mainHeaderBgClasses: '',
      mainHeaderRefreshBtnClasses: '',
      hasParentHostProblems: false,
      parenthosts: [],
      parentHostProblems: {},
      loading: true,
      hostDetails: [],
      mergedHost: [],
      hoststatus: [],
      acknowledgement: [],
      downtime: [],
      checkPeriod: [],
      notifyPeriod: [],
      services: [],
      services_paging: {
        page: 1,
        pageCount: 1
      },
      hostId: Number,
      servicenameFilter: '',
      servicestates: [],
      servicestateFilterOptions: [
        {
          label: this.$t('ok'),
          value: 'ok'
        },
        {
          label: this.$t('warning'),
          value: 'warning'
        },
        {
          label: this.$t('critical'),
          value: 'critical'
        },
        {
          label: this.$t('unknown'),
          value: 'unknown'
        }
      ],
      servicesFilter: {
        filter: {
          hostsId: [],
          servicename: '',
          current_state: [],
        }
      },
      breadcrumps: [
        {
          text: 'Hosts',
          to: {name: 'hostsIndex'}
        },
        {
          text: 'Host Details',
          active: true
        }
      ]
    };
  },
  methods: {
    goBack: function() {
      this.$router.back();
    },

    setTab: function(val) {
      this.selectedTab = val;
    },

    contactInfo: function(contact) {
      this.$store.commit('setContactInfo', contact);
      this.$router.push({name: 'contactInfo'});
    },

    linkClass: function() {
      return ['lesspadding'];
    },
    getData() {
      let loader = this.$loading.show();
      this.loading = true;
      this.hostId = this.$route.params.id;
      axios.post('/hosts/details.json', null, {
        params: {
          id: this.hostId
        }
      })
          .then(response => {
            this.loading = false;
            this.hostDetails = response.data.hostDetails ?? [];
            this.mergedHost = response.data.hostDetails.mergedHost ?? [];
            this.hoststatus = response.data.hostDetails.hoststatus ?? [];
            this.checkPeriod = response.data.hostDetails.checkPeriod ?? [];
            this.notifyPeriod = response.data.hostDetails.checkPeriod ?? [];
            this.parenthosts = response.data.hostDetails.parenthosts ?? [];
            this.parentHoststatus = response.data.hostDetails.parentHostStatus ?? [];
            if('acknowledgement' in response.data.hostDetails) {
              this.acknowledgement = response.data.hostDetails.acknowledgement;
            }
            if('downtime' in response.data.hostDetails) {
              this.downtime = response.data.hostDetails.downtime;
            }
            if(this.services_paging.page === 1) {
              this.getServiceData(this.services_paging.page);
            }

            this.mainHeaderBgClasses = this.getHeaderBgClass();
            this.mainHeaderRefreshBtnClasses = this.getHeaderRefreshBtnClass();
            this.buildParentHostProblems();


          })
          .catch(error => {
            useToast().error(this.$t('error'), {
              timeout: 2000
            });
          })
          .finally(() => {
            loader.hide();
          });
    },
    getServiceData(page) {
      let loader = this.$loading.show();
      this.servicesFilter.filter.hostsId = [this.hostId]
      axios.post('/services/index.json', {
        filter: this.servicesFilter,
        page: page
      }, {
        params: {}
      })
          .then(response => {
            if(response.status === 200) {
              this.services = response.data.servicedata.all_services ?? [];
              this.services_paging = response.data.servicedata.paging ?? {
                page: 1,
                pageCount: 1
              };
            } else {
              useToast().error(this.$t('no-refresh'), {
                timeout: 2000
              });
            }
          })
          .catch(error => {
            useToast().error(this.$t('error'), {
              timeout: 2000
            });
          })
          .finally(() => {
            loader.hide();
          });
    },
    allServicesFilter() {
      this.servicenameFilter = '';
      this.servicestates = [];
      this.getServiceData(1);
    },
    refreshData() {
      this.getData()
      this.setTab('tab1');
    },
    getHeaderBgClass() {
      if(!this.hoststatus.isInMonitoring) {
        return 'bg-primary shadow-lg-primary text-white';
      }

      switch(this.hoststatus.currentState) {
        case 0:
          return 'bg-up shadow-lg-up text-white';

        case 1:
          return 'bg-down shadow-lg-down text-white';

        default:
          return 'bg-unreachable shadow-lg-unreachable text-dark';
      }
    },
    getHeaderRefreshBtnClass() {
      if(!this.hoststatus.isInMonitoring) {
        return 'btn-primary';
      }

      switch(this.hoststatus.currentState) {
        case 0:
          return 'btn-success';

        case 1:
          return 'btn-danger';

        default:
          return 'btn-secundary';
      }
    },
    buildParentHostProblems() {
      this.hasParentHostProblems = false;
      this.parentHostProblems = {};
      for(var key in this.parenthosts) {
        let parentHostUuid = this.parenthosts[key].uuid;
        if(this.parentHoststatus.hasOwnProperty(parentHostUuid)) {
          if(this.parentHoststatus[parentHostUuid].currentState > 0) {
            this.parentHostProblems[parentHostUuid] = {
              id: this.parenthosts[key].id,
              name: this.parenthosts[key].name,
              state: this.parentHoststatus[parentHostUuid].currentState
            };
            this.hasParentHostProblems = true;
          }
        }
      }
    }
  },
  computed: {
    actionPermissions: function() {
      return this.$store.state.canSubmitCommands;

    }
  },
  watch: {
    // Hacky? Otherwise Vue will not reload the data if a state links to itself
    // /hosts/details/4 links to /hosts/details/10
    '$route.params.id': {
      handler: function() {
        this.getData();
      }
      //deep: true,
      //immediate: true
    },
    servicenameFilter: debounce(function(newValue){
      if(newValue != null){
        this.servicesFilter.filter.servicename = newValue;
        this.getServiceData(1);
      }
    }, 700),
    servicestates: function(newValue){
      if(newValue != null){
        this.servicesFilter.filter.current_state = newValue.map(obj => obj.value);
        this.getServiceData(1);
      }
    },
  },
  mounted() {
    this.getData();
  }

}
</script>

<style scoped>

</style>
